<template>
  <TopNav :progress="0" active-page="exposicoes" />

  <template v-if="id === 'omashu-express-unip'">
    <ExposicaoOmashuTicket @omashu-open="onOmashuOpen" @omashu-close="onOmashuClose" />

    <section v-if="!omashuOpen" class="hero-track" ref="heroRef">
      <p class="scroll-hint inter-regular">{{ $t('exposicoesPage.scrollHint') }}</p>
    </section>

    <ExposicaoOmashuExpressInfo v-if="!omashuOpen" ref="infoRef" />
  </template>

  <section v-else class="not-found">
    <p class="inter-regular">{{ $t('exposicoesPage.notFound') }}</p>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import TopNav from '@/components/TopNav.vue'
import ExposicaoOmashuTicket from '@/components/ExposicaoOmashuTicket.vue'
import ExposicaoOmashuExpressInfo from '@/components/ExposicaoOmashuExpressInfo.vue'

defineProps({
  id: {
    type: String,
    default: ''
  }
})

const omashuOpen = ref(false)
const heroRef    = ref(null)
const infoRef    = ref(null)
const atHero     = ref(true)
let isAnimatingScroll = false

const onOmashuOpen = () => {
  omashuOpen.value = true
  document.body.style.overflow = 'hidden'
}

const onOmashuClose = () => {
  omashuOpen.value = false
  document.body.style.overflow = ''
}

const getInfoEl = () => infoRef.value?.$el ?? null

const smoothScrollTo = (top) => new Promise((resolve) => {
  isAnimatingScroll = true
  const start = window.scrollY
  const distance = top - start
  const duration = 650
  const startTime = performance.now()

  const tick = (now) => {
    const t = Math.min(1, (now - startTime) / duration)
    const eased = 1 - Math.pow(1 - t, 3)
    window.scrollTo(0, start + distance * eased)
    if (t < 1) {
      requestAnimationFrame(tick)
    } else {
      isAnimatingScroll = false
      resolve()
    }
  }

  requestAnimationFrame(tick)
})

const goToInfo = () => {
  const el = getInfoEl()
  if (!el || isAnimatingScroll) return
  atHero.value = false
  smoothScrollTo(el.offsetTop)
}

const goToHero = () => {
  if (isAnimatingScroll) return
  atHero.value = true
  smoothScrollTo(0)
}

const isAtInfoTop = () => {
  const el = getInfoEl()
  return !!el && window.scrollY <= el.offsetTop + 1
}

const handleWheel = (e) => {
  if (omashuOpen.value || isAnimatingScroll) return

  if (atHero.value && e.deltaY > 0) {
    e.preventDefault()
    goToInfo()
    return
  }

  if (!atHero.value && e.deltaY < 0 && isAtInfoTop()) {
    e.preventDefault()
    goToHero()
  }
}

let touchStartY = 0
const handleTouchStart = (e) => {
  touchStartY = e.touches[0]?.clientY ?? 0
}

const handleTouchMove = (e) => {
  if (omashuOpen.value || isAnimatingScroll) return
  const currentY = e.touches[0]?.clientY ?? 0
  const delta = touchStartY - currentY

  if (atHero.value && delta > 8) {
    e.preventDefault()
    goToInfo()
    return
  }

  if (!atHero.value && delta < -8 && isAtInfoTop()) {
    e.preventDefault()
    goToHero()
  }
}

onMounted(() => {
  window.addEventListener('wheel', handleWheel, { passive: false })
  window.addEventListener('touchstart', handleTouchStart, { passive: true })
  window.addEventListener('touchmove', handleTouchMove, { passive: false })
})

onUnmounted(() => {
  window.removeEventListener('wheel', handleWheel)
  window.removeEventListener('touchstart', handleTouchStart)
  window.removeEventListener('touchmove', handleTouchMove)
  document.body.style.overflow = ''
})
</script>

<style scoped>
.hero-track {
  min-height: 100vh;
  background:
    radial-gradient(ellipse 70% 50% at 10% 100%, rgba(251, 191, 36, 0.12), transparent 70%),
    var(--color-bg-dark);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.scroll-hint {
  position: absolute;
  bottom: 32px;
  left: 0;
  right: 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  animation: hint-bob 2s ease-in-out infinite;
}

@keyframes hint-bob {
  0%, 100% { transform: translateY(0); opacity: 0.6; }
  50%       { transform: translateY(6px); opacity: 1; }
}

.not-found {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-light);
  background: var(--color-bg-dark);
}
</style>
